import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gamePlanTrackerService } from '@/services/game-plan-tracker.service';
import type {
  CurrentWeekResponse,
  GameJourney,
  GoalBoard,
  LogActivityDto,
  OnboardGamePlanDto,
  OverviewResponse,
  PlanImpact,
  SetTaskCompletionDto,
  UpdatePlanSettingsDto,
} from '@/types/gamePlanTracker';

const key = {
  overview: (locationId: string) => ['game-plan', 'overview', locationId],
  currentWeek: (locationId: string, week?: number) => [
    'game-plan',
    'current-week',
    locationId,
    week ?? 'current',
  ],
};

// Onboarding status + (when onboarded) the live Current Week payload.
export function useGamePlanOverview(locationId: string | undefined) {
  return useQuery<OverviewResponse>({
    queryKey: key.overview(locationId ?? ''),
    queryFn: () => gamePlanTrackerService.getOverview(locationId as string),
    enabled: Boolean(locationId),
  });
}

// Current Week board for a specific week (omit for the live current week).
export function useCurrentWeek(
  locationId: string | undefined,
  week?: number,
  enabled = true,
) {
  return useQuery<CurrentWeekResponse>({
    queryKey: key.currentWeek(locationId ?? '', week),
    queryFn: () =>
      gamePlanTrackerService.getCurrentWeek(locationId as string, week),
    enabled: Boolean(locationId) && enabled,
  });
}

export function useGoalBoard(locationId: string | undefined) {
  return useQuery<GoalBoard>({
    queryKey: ['game-plan', 'goal-board', locationId ?? ''],
    queryFn: () => gamePlanTrackerService.getGoalBoard(locationId as string),
    enabled: Boolean(locationId),
  });
}

export function useJourney(locationId: string | undefined) {
  return useQuery<GameJourney>({
    queryKey: ['game-plan', 'journey', locationId ?? ''],
    queryFn: () => gamePlanTrackerService.getJourney(locationId as string),
    enabled: Boolean(locationId),
  });
}

export function useOnboardGamePlan(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: OnboardGamePlanDto) =>
      gamePlanTrackerService.onboard(locationId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-plan'] });
    },
  });
}

/**
 * Live "what would change" preview for an in-flight goal / date edit.
 *
 * Idle until the owner has actually proposed something different, so simply
 * opening the editor costs nothing. The query key carries the proposal, so each
 * nudge of the stepper is its own cached answer and stepping back and forth is
 * instant.
 */
export function usePlanImpact(
  locationId: string | undefined,
  goal?: number,
  grandOpeningDate?: string,
  enabled = true,
) {
  return useQuery<PlanImpact>({
    queryKey: ['game-plan', 'impact', locationId ?? '', goal, grandOpeningDate],
    queryFn: () =>
      gamePlanTrackerService.getPlanImpact(
        locationId as string,
        goal,
        grandOpeningDate,
      ),
    enabled: Boolean(locationId) && enabled,
    // A rejected proposal (e.g. a date inside the next week) is a 400 the form
    // surfaces as a hint — retrying it just delays the message.
    retry: false,
  });
}

export function useUpdatePlanSettings(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdatePlanSettingsDto) =>
      gamePlanTrackerService.updatePlanSettings(locationId, dto),
    onSuccess: () => {
      // The goal and the date feed every Game Plan surface — refetch the lot.
      qc.invalidateQueries({ queryKey: ['game-plan'] });
    },
  });
}

export function useLogActivity(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: LogActivityDto) =>
      gamePlanTrackerService.logActivity(locationId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-plan'] });
    },
  });
}

export function useUpdateActivity(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: LogActivityDto }) =>
      gamePlanTrackerService.updateActivity(locationId, id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-plan'] });
    },
  });
}

export function useDeleteActivity(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      gamePlanTrackerService.deleteActivity(locationId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-plan'] });
    },
  });
}

export function useSetTaskCompletion(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: SetTaskCompletionDto) =>
      gamePlanTrackerService.setTaskCompletion(locationId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['game-plan'] });
    },
  });
}
